import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  PlayIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const getInitials = (name) =>
  String(name || 'TM')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'TM';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatCount = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const clampProgress = (value) => Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));

const getTodayLabel = () =>
  new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const getRoleTagline = (role) => {
  if (['admin', 'org_admin', 'app_manager'].includes(role)) {
    return "Monitor your organization's activity and act where it matters.";
  }
  if (role === 'parent') {
    return "Stay in sync with your player's schedule, messages, and upcoming events.";
  }
  if (role === 'player') {
    return 'Check your schedule, team updates, and messages from your coaches.';
  }
  return 'Manage events, payments, and communication from one place.';
};

const getPriorityMessage = (stats) => {
  if ((stats?.upcomingEvents || 0) > 0) {
    return `You have ${formatCount(stats.upcomingEvents)} event${stats.upcomingEvents === 1 ? '' : 's'} waiting on RSVP decisions.`;
  }

  if ((stats?.unreadMessages || 0) > 0) {
    return `${formatCount(stats.unreadMessages)} unread team message${stats.unreadMessages === 1 ? ' needs' : 's need'} attention.`;
  }

  if ((stats?.pendingPayments || 0) > 0) {
    return `${formatCount(stats.pendingPayments)} pending payment${stats.pendingPayments === 1 ? ' is' : 's are'} still open.`;
  }

  return 'Your dashboard is clear. Use the quick actions below to keep the team moving.';
};

const getActivityIcon = (iconName) => {
  switch (iconName) {
    case 'ChatBubbleLeftRightIcon':
      return ChatBubbleLeftRightIcon;
    case 'CurrencyDollarIcon':
      return CurrencyDollarIcon;
    case 'PlayIcon':
      return PlayIcon;
    case 'CalendarIcon':
    default:
      return CalendarIcon;
  }
};

const getActivityTone = (color) => {
  if (color?.includes('emerald') || color?.includes('green')) {
    return {
      iconClass: 'text-emerald-200',
      badgeClass: 'border-emerald-300/15 bg-emerald-400/10 text-emerald-100',
    };
  }

  if (color?.includes('blue')) {
    return {
      iconClass: 'text-sky-200',
      badgeClass: 'border-sky-300/15 bg-sky-400/10 text-sky-100',
    };
  }

  if (color?.includes('purple')) {
    return {
      iconClass: 'text-fuchsia-200',
      badgeClass: 'border-fuchsia-300/15 bg-fuchsia-400/10 text-fuchsia-100',
    };
  }

  return {
    iconClass: 'text-slate-200',
    badgeClass: 'border-white/10 bg-white/5 text-slate-100',
  };
};

const getEventTone = (type) => {
  if (type === 'game') {
    return 'border-rose-300/15 bg-rose-400/10 text-rose-100';
  }

  if (type === 'practice') {
    return 'border-sky-300/15 bg-sky-400/10 text-sky-100';
  }

  return 'border-white/10 bg-white/5 text-slate-100';
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    upcomingEvents: 0,
    pendingPayments: 0,
    totalBudget: 0,
    spentBudget: 0,
    teamMembers: 0,
    unreadMessages: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      setLoading(false);
      return;
    }

    const fetchDashboardData = async () => {
      try {
        const response = await fetch('/api/dashboard/stats', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setStats(data.stats);
          setRecentActivities(data.recentActivities);
          setUpcomingEvents(data.upcomingEventsList);
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 401) {
            return;
          }
          console.error('Failed to fetch dashboard data:', errorData.message || response.statusText);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/organizations/my-organizations', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const orgUsers = Array.isArray(data) ? data : [];
        const primaryOrg = orgUsers.find(ou => ou?.is_primary) || orgUsers[0];
        const orgSponsors = primaryOrg?.Organization?.sponsors || primaryOrg?.sponsors || [];
        setSponsors(Array.isArray(orgSponsors) ? orgSponsors : []);
      })
      .catch(() => {});
  }, [user]);

  const quickActions = useMemo(
    () => [
      {
        name: 'AI Video Analysis',
        href: '/video-analysis',
        icon: SparklesIcon,
        description: 'Run the new coaching workflow on your latest clip.',
        featured: true,
        iconWrapClass: 'bg-gradient-to-br from-emerald-300 via-sky-300 to-orange-300 text-slate-950',
      },
      {
        name: 'Send Team Message',
        href: '/chat',
        icon: ChatBubbleLeftRightIcon,
        description: 'Keep parents and players aligned.',
        iconWrapClass: 'bg-sky-400/15 text-sky-100',
      },
      {
        name: 'Schedule Event',
        href: '/events',
        icon: CalendarIcon,
        description: 'Add the next practice, game, or meeting.',
        iconWrapClass: 'bg-emerald-400/15 text-emerald-100',
      },
      {
        name: 'Upload Video',
        href: '/videos',
        icon: PlayIcon,
        description: 'Share drills, footage, and homework.',
        iconWrapClass: 'bg-fuchsia-400/15 text-fuchsia-100',
      },
      {
        name: 'Manage Budget',
        href: '/payments',
        icon: CurrencyDollarIcon,
        description: 'Review payments and budget movement.',
        iconWrapClass: 'bg-amber-400/15 text-amber-100',
      },
    ],
    []
  );

  const budgetProgressRaw = stats.totalBudget > 0 ? (stats.spentBudget / stats.totalBudget) * 100 : 0;
  const budgetProgress = clampProgress(budgetProgressRaw);
  const remainingBudget = Math.max(0, Number(stats.totalBudget || 0) - Number(stats.spentBudget || 0));
  const priorityMessage = getPriorityMessage(stats);

  const statCards = [
    {
      name: 'RSVP Queue',
      value: formatCount(stats.upcomingEvents),
      href: '/events?filter=unrsvp',
      icon: CalendarIcon,
      detail: 'Events waiting on a response',
      toneClass: 'border-sky-300/15 bg-sky-400/10 text-sky-100',
    },
    {
      name: 'Unread Messages',
      value: formatCount(stats.unreadMessages),
      href: '/chat',
      icon: ChatBubbleLeftRightIcon,
      detail: 'Conversations to catch up on',
      toneClass: 'border-emerald-300/15 bg-emerald-400/10 text-emerald-100',
    },
    {
      name: 'Pending Payments',
      value: formatCount(stats.pendingPayments),
      href: '/payments',
      icon: CurrencyDollarIcon,
      detail: 'Outstanding payment activity',
      toneClass: 'border-amber-300/15 bg-amber-400/10 text-amber-100',
    },
    {
      name: 'Team Members',
      value: formatCount(stats.teamMembers),
      href: '/roster',
      icon: UserGroupIcon,
      detail: 'Active people in the current team',
      toneClass: 'border-fuchsia-300/15 bg-fuchsia-400/10 text-fuchsia-100',
    },
  ];

  if (loading) {
    return (
      <div className="min-h-[320px] overflow-hidden rounded-[32px] border border-slate-800 bg-slate-950 shadow-[0_30px_80px_rgba(15,23,42,0.45)]">
        <div className="flex min-h-[320px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/10 border-t-emerald-300" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full overflow-hidden rounded-[32px] border border-slate-800 bg-slate-950 text-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.45)]">
      <div className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(56,189,248,0.12),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(251,146,60,0.12),_transparent_34%)]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/5 to-transparent" />

        <div className="relative p-4 sm:p-6 lg:p-8">
          <div className="border-b border-white/10 pb-6">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_360px]">
              <div className="flex flex-col gap-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.36em] text-emerald-300/80">{getTodayLabel()}</p>
                  <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                    Team command center for {user?.name || 'your staff'}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                    {getRoleTagline(user?.role)}
                  </p>
                </div>

                <div className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-300 via-sky-300 to-orange-300 text-base font-semibold text-slate-950">
                      {getInitials(user?.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Priority Lane</p>
                      <div className="mt-3 rounded-[22px] bg-slate-800/95 px-4 py-3 text-[15px] leading-7 text-slate-100 shadow-inner shadow-black/20">
                        {priorityMessage}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Team Pulse</p>
                <div className="mt-5 grid gap-4">
                  <div className="rounded-[24px] border border-white/10 bg-slate-950/70 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium uppercase tracking-[0.24em] text-slate-400">Budget Flow</p>
                        <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white">{formatCurrency(stats.spentBudget)}</h2>
                      </div>
                      <span className="rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200">
                        {Math.round(budgetProgress)}% used
                      </span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-300 via-sky-300 to-orange-300 transition-all duration-300"
                        style={{ width: `${budgetProgress}%` }}
                      />
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-3 text-sm text-slate-300">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Total</p>
                        <p className="mt-1 font-medium text-white">{formatCurrency(stats.totalBudget)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Spent</p>
                        <p className="mt-1 font-medium text-white">{formatCurrency(stats.spentBudget)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Remaining</p>
                        <p className="mt-1 font-medium text-white">{formatCurrency(remainingBudget)}</p>
                      </div>
                    </div>
                  </div>

                  <a
                    href="/video-analysis"
                    className="group rounded-[24px] border border-emerald-300/15 bg-emerald-400/10 p-5 transition hover:border-emerald-300/30 hover:bg-emerald-400/15"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-100/70">Featured Workflow</p>
                        <h2 className="mt-3 text-xl font-semibold text-white">AI Video Analysis</h2>
                        <p className="mt-2 text-sm leading-6 text-emerald-50/85">
                          Jump straight into the new clip review experience from the dashboard.
                        </p>
                      </div>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-slate-950">
                        <SparklesIcon className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="mt-4 inline-flex items-center text-sm font-medium text-emerald-100">
                      Open analysis
                      <ChevronRightIcon className="ml-1 h-4 w-4 transition group-hover:translate-x-0.5" />
                    </div>
                  </a>
                </div>
              </section>
            </div>
          </div>

          {/* Sponsor / Partner Banner — only rendered when sponsors exist */}
          {sponsors.length > 0 && (() => {
            const minItems = 6;
            const copies = Math.ceil(minItems / sponsors.length);
            const scrollItems = Array.from({ length: copies * 2 }, (_, i) => sponsors[i % sponsors.length]);
            const duration = Math.max(18, sponsors.length * 6);
            return (
              <section className="mt-6 overflow-hidden rounded-[24px] border border-white/10 bg-slate-900/80">
                <div className="px-5 pt-4 pb-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Sponsors &amp; Partners</p>
                </div>
                <div className="relative overflow-hidden pb-4">
                  <div
                    className="flex items-center gap-4 whitespace-nowrap"
                    style={{ animation: `marquee ${duration}s linear infinite`, width: 'max-content' }}
                    onMouseEnter={e => { e.currentTarget.style.animationPlayState = 'paused'; }}
                    onMouseLeave={e => { e.currentTarget.style.animationPlayState = 'running'; }}
                  >
                    {scrollItems.map((sponsor, i) => (
                      <a
                        key={`${sponsor.id}-${i}`}
                        href={sponsor.website || undefined}
                        target={sponsor.website ? '_blank' : undefined}
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-3 rounded-[18px] border border-white/10 bg-slate-800/60 px-5 py-3 transition hover:border-white/20 hover:bg-slate-800 ${sponsor.website ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        {sponsor.logo && (
                          <img
                            src={sponsor.logo}
                            alt={sponsor.name}
                            className="h-7 w-auto max-w-[80px] object-contain opacity-80"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span className="text-sm font-medium text-slate-300">{sponsor.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

          <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((card) => (
              <a
                key={card.name}
                href={card.href}
                className="group rounded-[24px] border border-white/10 bg-slate-900/75 p-5 transition hover:border-white/20 hover:bg-slate-900"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">{card.name}</p>
                    <p className="mt-3 text-3xl font-semibold tracking-tight text-white">{card.value}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-400">{card.detail}</p>
                  </div>
                  <div className={`rounded-2xl border p-3 ${card.toneClass}`}>
                    <card.icon className="h-6 w-6" />
                  </div>
                </div>
              </a>
            ))}
          </section>

          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
            <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Quick Actions</p>
                  <h2 className="mt-2 text-xl font-semibold text-white">Move the team forward</h2>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                {quickActions
                  .filter((action) => action.featured)
                  .map((action) => (
                    <a
                      key={action.name}
                      href={action.href}
                      className="group rounded-[26px] border border-white/10 bg-slate-950/70 p-5 transition hover:border-white/20 hover:bg-slate-950"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className={`flex h-14 w-14 items-center justify-center rounded-full ${action.iconWrapClass}`}>
                          <action.icon className="h-7 w-7" />
                        </div>
                      </div>
                      <h3 className="mt-6 text-2xl font-semibold text-white">{action.name}</h3>
                      <p className="mt-3 max-w-md text-sm leading-6 text-slate-300">{action.description}</p>
                      <div className="mt-5 inline-flex items-center text-sm font-medium text-white">
                        Launch
                        <ChevronRightIcon className="ml-1 h-4 w-4 transition group-hover:translate-x-0.5" />
                      </div>
                    </a>
                  ))}

                <div className="grid gap-3">
                  {quickActions
                    .filter((action) => !action.featured)
                    .map((action) => (
                      <a
                        key={action.name}
                        href={action.href}
                        className="group rounded-[22px] border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/[0.08]"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${action.iconWrapClass}`}>
                            <action.icon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white">{action.name}</p>
                            <p className="mt-1 text-sm text-slate-400">{action.description}</p>
                          </div>
                          <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-slate-300" />
                        </div>
                      </a>
                    ))}
                </div>
              </div>
            </section>

            <section className="rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Upcoming Events</p>
              <h2 className="mt-2 text-xl font-semibold text-white">What’s next on the calendar</h2>

              <div className="mt-5 grid gap-3">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event) => (
                    <div
                      key={event.id}
                      className="rounded-[22px] border border-white/10 bg-slate-950/70 p-4 transition hover:border-white/20"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white">{event.title}</p>
                          <p className="mt-1 text-sm text-slate-400">
                            {event.date}
                            {event.location ? ` • ${event.location}` : ''}
                          </p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] ${getEventTone(event.type)}`}>
                          {event.type || 'event'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-[24px] border border-dashed border-white/10 px-4 py-10 text-center text-sm text-slate-400">
                    No upcoming events are scheduled yet.
                  </div>
                )}
              </div>
            </section>
          </div>

          <section className="mt-6 rounded-[28px] border border-white/10 bg-slate-900/80 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-400">Recent Activities</p>
            <h2 className="mt-2 text-xl font-semibold text-white">Latest team movement</h2>

            <div className="mt-5 grid gap-3">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => {
                  const IconComponent = getActivityIcon(activity.icon);
                  const tone = getActivityTone(activity.color);

                  return (
                    <div
                      key={activity.id}
                      className="flex flex-col gap-3 rounded-[22px] border border-white/10 bg-slate-950/70 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 items-start gap-3">
                        <div className={`rounded-2xl border p-3 ${tone.badgeClass}`}>
                          <IconComponent className={`h-5 w-5 ${tone.iconClass}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-white">{activity.message}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{activity.type || 'activity'}</p>
                        </div>
                      </div>
                      <span className="text-sm text-slate-400">{activity.time}</span>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-[24px] border border-dashed border-white/10 px-4 py-10 text-center text-sm text-slate-400">
                  No recent activity yet.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
